<script setup lang="ts">
	import { computed } from 'vue';
	import { useI18n } from 'vue-i18n';
	import { MonizzeLogo, MzHeader, MzLang, MzLayout } from '@monizze/monizze-components';
	import { useAuthStore } from '@/stores/auth.ts';
	import Logout from '@/pages/Logout.vue';
	import DynamicBreadcrumb from '@/components/DynamicBreadcrumb.vue';
	import { getPlatformComingFrom } from '@/utils/platformComingFrom.ts';

	const { t, locale, fallbackLocale } = useI18n();
	const authStore = useAuthStore();

	// Merchants coming from merchant-area get a merchant image and tagline instead of the employers/employees ones.
	const isMerchant = computed(() => getPlatformComingFrom() === 'login.page.platform.merchant');

	const resolvedImage = computed(() => {
		if (authStore.isLux) return '/Background/up-lux.jpg';
		return isMerchant.value ? '/Merchant/merchant-hero.webp' : '/CustomerOnboarding/customer-onboarding-signup.png';
	});
	const tagline = computed(() => t(isMerchant.value ? 'layout.brand.tagline-merchant' : 'layout.brand.tagline'));

	const updateLocale = (newLocale: string): void => {
		locale.value = newLocale;
		if (authStore.isLux) {
			fallbackLocale.value = newLocale.substring(0, 2);
		}
	};
</script>

<template>
	<div class="min-h-screen lg:grid lg:grid-cols-2">
		<aside class="hidden lg:block bg-cover bg-center relative" :style="{ backgroundImage: `url(${resolvedImage})` }">
			<div
				class="absolute inset-x-0 bottom-0 top-1/2 pointer-events-none"
				style="background: linear-gradient(180deg, rgba(55, 64, 63, 0) 0%, #37403f 100%); mix-blend-mode: multiply"
			></div>
			<div class="absolute bottom-16 left-10 right-10 text-white">
				<p class="font-['Roboto_Slab'] font-bold text-[48px] leading-[56px]">
					{{ tagline }}
				</p>
			</div>
		</aside>
		<mz-layout variant="stacked" :sticky="false" :ui="{ root: 'bg-bg-default-light', main: 'flex items-center justify-center px-4 py-10' }">
			<template #header>
				<mz-header variant="bar" class="w-full px-6 sm:px-10" :ui="{ innerHeader: 'justify-between' }">
					<template #left>
						<router-link to="/" class="self-center">
							<img v-if="authStore.isLux" src="/UpLux.svg" alt="" class="h-10 w-auto" />
							<monizze-logo v-else class="h-6 w-auto" />
						</router-link>
					</template>
					<template #right>
						<div class="flex items-center gap-4">
							<mz-lang :availables-locales="authStore.availableLanguages" @change="updateLocale" />
							<logout />
						</div>
					</template>
				</mz-header>
			</template>
			<div class="w-full max-w-md flex flex-col gap-6">
				<dynamic-breadcrumb />
				<router-view />
			</div>
		</mz-layout>
	</div>
</template>
